import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Trophy, AlertTriangle, Loader2, Swords } from 'lucide-react';
import { championshipsApi } from '../lib/api';
import { toast } from 'sonner';

/**
 * Anteprima del bracket knockout PRIMA della generazione.
 * Mostra:
 *  - squadre qualificate dirette (con girone, posizione, statistiche)
 *  - squadre ripescate (best Nth-placed)
 *  - accoppiamenti del primo turno
 * Solo dopo la conferma esplicita chiama l'endpoint di generazione con force=true.
 */
const Row = ({ q, isItalian }) => (
  <div
    className={`flex items-center justify-between gap-2 px-2.5 py-1.5 text-xs border border-white/5 ${
      q.rescued ? 'bg-[#FF00E5]/5 border-[#FF00E5]/20' : 'bg-[#050508]'
    }`}
    data-testid={`preview-qualifier-${q.club_id}`}
  >
    <div className="flex items-center gap-2 min-w-0 flex-1">
      <span className={`shrink-0 px-1.5 py-0.5 rounded text-[10px] font-bold ${
        q.rescued ? 'bg-[#FF00E5]/20 text-[#FF00E5]' : 'bg-[#00F0FF]/10 text-[#00F0FF]'
      }`}>
        {q.group} · {q.position}{q.position === 1 ? '°' : 'ª'}
      </span>
      {q.club_logo ? (
        <img src={q.club_logo} alt="" className="w-5 h-5 rounded object-cover shrink-0" />
      ) : (
        <div className="w-5 h-5 rounded bg-slate-800 shrink-0" />
      )}
      <span className="text-white font-medium truncate">{q.club_name}</span>
      {q.rescued && (
        <span className="shrink-0 px-1 py-0 text-[9px] uppercase tracking-wider bg-[#FF00E5]/20 text-[#FF00E5] rounded">
          {isItalian ? 'ripescata' : 'rescued'}
        </span>
      )}
    </div>
    <div className="flex items-center gap-2 text-[10px] text-slate-400 shrink-0">
      <span>{q.points} pt</span>
      <span>DR {q.gd >= 0 ? '+' : ''}{q.gd}</span>
      <span>GF {q.gf}</span>
    </div>
  </div>
);

export const KnockoutPreviewDialog = ({ open, onOpenChange, championship, isItalian, onConfirmed }) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !championship) {
      setPreview(null);
      setError(null);
      return;
    }
    const fetchPreview = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await championshipsApi.knockoutPreview(championship.championship_id);
        setPreview(res.data);
      } catch (err) {
        setError(err?.response?.data?.detail || (isItalian ? 'Errore nel calcolo anteprima' : 'Preview error'));
      } finally {
        setLoading(false);
      }
    };
    fetchPreview();
  }, [open, championship, isItalian]);

  const handleConfirm = async () => {
    if (!championship) return;
    setSubmitting(true);
    try {
      const res = await championshipsApi.generateKnockoutPhase(championship.championship_id, { force: true });
      toast.success(res.data?.message || (isItalian ? 'Bracket creato!' : 'Bracket created!'));
      onOpenChange(false);
      if (onConfirmed) onConfirmed(championship.championship_id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || (isItalian ? 'Errore generazione' : 'Generation error'));
    } finally {
      setSubmitting(false);
    }
  };

  const isAlreadyKO = championship?.phase === 'knockout';
  const rescuedCount = preview?.rescued?.length || 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0a0d15] border-white/10 text-white max-w-2xl max-h-[90vh] flex flex-col p-0">
        <DialogHeader className="px-6 py-4 border-b border-white/5 shrink-0">
          <DialogTitle className="flex items-center gap-2 text-base">
            <Trophy size={18} className="text-[#FFEA00]" />
            {isItalian ? 'Anteprima Bracket' : 'Bracket Preview'}
            {championship && <span className="text-slate-400 font-normal text-sm">— {championship.name}</span>}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {loading && (
            <div className="flex items-center justify-center gap-2 py-12 text-slate-400">
              <Loader2 size={18} className="animate-spin" />
              {isItalian ? 'Calcolo classifiche e accoppiamenti…' : 'Computing standings and pairings…'}
            </div>
          )}

          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded text-red-300 text-sm flex items-start gap-2">
              <AlertTriangle size={16} className="shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {!loading && !error && preview && (
            <>
              {isAlreadyKO && (
                <div className="p-2.5 bg-orange-500/10 border border-orange-500/30 rounded text-orange-300 text-[11px] flex items-start gap-2" data-testid="preview-regen-warning">
                  <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                  <span>
                    {isItalian
                      ? 'Un bracket esiste già. Confermando, le partite knockout attuali verranno CANCELLATE e ricostruite con la nuova selezione.'
                      : 'A bracket already exists. Confirming will DELETE current knockout matches and rebuild with the new selection.'}
                  </span>
                </div>
              )}

              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="p-2 bg-[#050508] border border-white/5 rounded">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500">{isItalian ? 'Bracket' : 'Bracket'}</p>
                  <p className="text-lg font-heading text-[#FFEA00]">{preview.bracket_size}</p>
                </div>
                <div className="p-2 bg-[#050508] border border-white/5 rounded">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500">{isItalian ? 'Dirette' : 'Direct'}</p>
                  <p className="text-lg font-heading text-[#00F0FF]">{(preview.qualifiers?.length || 0) - rescuedCount}</p>
                </div>
                <div className="p-2 bg-[#050508] border border-white/5 rounded">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500">{isItalian ? 'Ripescate' : 'Rescued'}</p>
                  <p className="text-lg font-heading text-[#FF00E5]">{rescuedCount}</p>
                </div>
              </div>

              <div>
                <p className="text-[11px] uppercase tracking-wider text-slate-400 mb-2">
                  {isItalian ? 'Squadre qualificate' : 'Qualified teams'}
                </p>
                <div className="space-y-1">
                  {(preview.qualifiers || []).map((q) => (
                    <Row key={q.club_id} q={q} isItalian={isItalian} />
                  ))}
                </div>
              </div>

              <div>
                <p className="text-[11px] uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-2">
                  <Swords size={12} />
                  {isItalian ? 'Accoppiamenti primo turno' : 'First-round pairings'}
                </p>
                <div className="space-y-1">
                  {(preview.pairings || []).map((p, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between gap-2 px-2.5 py-1.5 bg-[#050508] border border-white/5 text-xs"
                      data-testid={`preview-pairing-${idx}`}
                    >
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        {p.home?.logo ? (
                          <img src={p.home.logo} alt="" className="w-5 h-5 rounded object-cover shrink-0" />
                        ) : (
                          <div className="w-5 h-5 rounded bg-slate-800 shrink-0" />
                        )}
                        <span className="text-white truncate">{p.home?.name || '?'}</span>
                      </div>
                      <span className="text-[#FF00E5] font-bold text-[10px] shrink-0 mx-2">VS</span>
                      <div className="flex items-center gap-2 min-w-0 flex-1 justify-end">
                        <span className="text-white truncate text-right">{p.away?.name || '?'}</span>
                        {p.away?.logo ? (
                          <img src={p.away.logo} alt="" className="w-5 h-5 rounded object-cover shrink-0" />
                        ) : (
                          <div className="w-5 h-5 rounded bg-slate-800 shrink-0" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        <DialogFooter className="px-6 py-4 border-t border-white/5 shrink-0 bg-[#0a0d15] gap-2 sm:gap-2">
          <Button
            variant="outline"
            className="border-white/10"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            data-testid="preview-cancel-btn"
          >
            {isItalian ? 'Annulla' : 'Cancel'}
          </Button>
          <Button
            className="btn-primary"
            onClick={handleConfirm}
            disabled={loading || submitting || !preview || !!error}
            data-testid="preview-confirm-btn"
          >
            {submitting ? (
              <Loader2 size={14} className="animate-spin mr-1" />
            ) : (
              <Swords size={14} className="mr-1" />
            )}
            {isItalian
              ? (isAlreadyKO ? 'Conferma e Rigenera' : 'Conferma e Genera Bracket')
              : (isAlreadyKO ? 'Confirm & Regenerate' : 'Confirm & Generate Bracket')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default KnockoutPreviewDialog;
